// src/pages/member/PointRequestForm.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../login/AuthProvider";
import { point } from "../../components/apipath";
import ChineseCoursePointsTables from "./components/CoursePointsTables";
import "./styles/point.scss";
import Swal from "sweetalert2";

export default function PointRequestForm() {
  const { token, mem_id, mem_status, authorized } = useAuth();
  const navigate = useNavigate();
  

  // ✅ 額外點數拿掉：BONUS_POINTS_PER_YEAR 不再使用

  // 哪些狀態算「已使用」（請依你後台實際狀態微調）
  const APPROVED_OR_USED_STATUSES = [
    "通過",
    "完成",
    "已扣點",
    "已開通",
    "審核通過",
    "approved",
    "done",
  ];

  // UI / 表單
  const [coursePoints, setCoursePoints] = useState([]);
  const [formData, setFormData] = useState({
    mem_id,
    core_subjects: [],
    specialty: "",
    core_subject: [],
    e_material: false,
    requested_points: 0,
    // ✅ 額外點數拿掉：bonus 不再需要
    addp: false, // 你原本有「是否使用加購點數」的 UI，先保留（其餘不變動）
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // 點數相關狀態
  const [basePoints, setBasePoints] = useState(0); // mem.total_points（不變動）= ✅「總點數」
  const [addonPoints, setAddonPoints] = useState(0); // 後端解析 addon_orders.memo（中文課程 xN）
  const [addonDetails, setAddonDetails] = useState([]); // 顯示加購明細
  const [usedPoints, setUsedPoints] = useState(0); // ✅「已申請使用點數」（核准/使用）加總
  const [availablePoints, setAvailablePoints] = useState(0); // ✅「剩餘可申請點數」= base + addon - used
  const [myRequests, setMyRequests] = useState([]); // point_requests
  const [manualPoints, setManualPoints] = useState(false); // ✅ 是否已手動改過點數

  // 計算一組課程需要的點數
  const calculateTotalPoints = (selected) =>
    (selected || []).reduce((sum, courseName) => {
      const hit = coursePoints.find((c) => c.course_name === courseName);
      return sum + (hit ? Number(hit.points || 0) : 0);
    }, 0);

  const inRange = (n, a, b) => n >= a && n <= b;
  const getCategory = (id) => {
    if (inRange(id, 1, 6)) return "CPA";
    if (inRange(id, 7, 9)) return "EA";
    if (inRange(id, 10, 11)) return "CMA";
    return null;
  };

  const groups = {
    CPA: coursePoints.filter((c) => getCategory(Number(c.id)) === "CPA"),
    EA: coursePoints.filter((c) => getCategory(Number(c.id)) === "EA"),
    CMA: coursePoints.filter((c) => getCategory(Number(c.id)) === "CMA"),
  };

  const specialtyOptions = groups.CPA
    .filter((c) => ["TCP", "BAR", "ISC"].includes(c.course_name))
    .map((c) => c.course_name);

  // 抓課程點數表
  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${point}course-points`);
        if (resp.data.status === 200) setCoursePoints(resp.data.data || []);
      } catch (e) {
        console.error("課程點數表讀取失敗", e);
      }
    })();
  }, []);

  // 讀「我的點數狀態」：以 /my-status（含 addon_points/addon_details）為主；沒有就 fallback /my-points
  useEffect(() => {
    (async () => {
      try {
        const candidates = [
          `${point}my-status/${mem_id}`, // 建議：回 total_points、request_history、addon_points、addon_details
          `${point}my-points/${mem_id}`, // 舊路由：只有 total_points、request_history
        ];

        let d = null;
        for (const url of candidates) {
          try {
            const res = await axios.get(url, { headers: { token } });
            if (res?.data?.status === 200 && res?.data?.data) {
              d = res.data.data;
              break;
            }
          } catch (_) {}
        }
        if (!d) throw new Error("無法取得個人點數狀態");

        // 1) 總點數（不變動）
        const base = Number(d.total_points ?? 0);
        setBasePoints(base);

        // 2) 申請紀錄
        const list = Array.isArray(d.request_history) ? d.request_history : [];
        setMyRequests(list);

        // 3) 已申請使用點數（只加總「已核准/已使用」狀態）
        const used = list.reduce((sum, r) => {
          const st = String(r?.status || "").trim();
          const isUsed = APPROVED_OR_USED_STATUSES.includes(st);
          return isUsed ? sum + Number(r?.requested_points || 0) : sum;
        }, 0);
        setUsedPoints(used);

        // 4) 加購點數（若舊路由沒有就 0）
        setAddonPoints(Number(d.addon_points ?? 0));
        setAddonDetails(Array.isArray(d.addon_details) ? d.addon_details : []);
      } catch (e) {
        console.error("my-status / my-points 讀取失敗", e);
        Swal.fire({
          icon: "error",
          title: "讀取失敗",
          text: "無法取得您的點數資訊，請稍後再試。",
          confirmButtonText: "確認",
        });
      }
    })();
  }, [mem_id, token]);
  useEffect(() => {
      if (!authorized || String(mem_status) !== "1") {
          Swal.fire({
              icon: 'warning',
              title: '學員專屬功能',
              text: '此頁面僅限學員使用，請先完成購買並登入',
              confirmButtonText: '了解',
              confirmButtonColor: '#224040',
          }).then(() => {
              navigate('/member/memberlogin');
          });
      }
  }, []);
  // ✅ 剩餘可申請點數＝總點數 + 加購 − 已申請使用
  useEffect(() => {
    const remaining = Math.max(
      0,
      Number(basePoints) + Number(addonPoints) - Number(usedPoints)
    );
    setAvailablePoints(remaining);
  }, [basePoints, addonPoints, usedPoints]);

  // 表單互動
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const updated = checked
        ? [...prev.core_subjects, value]
        : prev.core_subjects.filter((v) => v !== value);

      const combo = [...updated, prev.specialty].filter(Boolean);
      const auto = calculateTotalPoints(combo);

      return {
        ...prev,
        core_subjects: updated,
        core_subject: combo,
        requested_points: manualPoints ? prev.requested_points : auto, // ✅ 手動後不覆蓋
      };
    });
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let combo = prev.core_subject;
      if (name === "specialty") {
        combo = [...(prev.core_subjects || []), value].filter(Boolean);
      }

      const auto = calculateTotalPoints(combo);

      return {
        ...prev,
        [name]:
          name === "e_material"
            ? value === "true"
            : value === "true"
            ? true
            : value === "false"
            ? false
            : value,
        core_subject: combo,
        requested_points: manualPoints ? prev.requested_points : auto, // ✅ 手動後不覆蓋
      };
    });
  };
  const handleRequestedPointsChange = (e) => {
    setManualPoints(true);

    let v = e.target.value;

    // 允許空字串（使用者正在輸入）
    if (v === "") {
      setFormData((prev) => ({ ...prev, requested_points: "" }));
      return;
    }

    // 只收整數 & >=0
    let n = parseInt(v, 10);
    if (isNaN(n)) n = 0;
    if (n < 0) n = 0;

    setFormData((prev) => ({ ...prev, requested_points: n }));
  };

  // 送出申請：比較「本次要用」vs「剩餘可申請點數」
  const handleSubmit = async (e) => {
    e.preventDefault();

    const want = Number(formData.requested_points || 0);
    const canUse = Number(availablePoints || 0); // ✅ 這裡已變成「剩餘可申請點數」

    // if (want > canUse) {
    //   Swal.fire({
    //     icon: "warning",
    //     title: "點數不足",
    //     html: `
    //       <div class="text-start">
    //         <div>本次申請：<b>${want}</b> 點</div>
    //         <div>剩餘可申請：<b>${canUse}</b> 點（= 總點數 ${basePoints} + 加購 ${addonPoints} − 已申請使用 ${usedPoints}）</div>
    //       </div>`,
    //     confirmButtonText: "確認",
    //   });
    //   return;
    // }

    setIsSubmitting(true);
    setSuccessMessage("");

    try {
      const payload = {
        mem_id: mem_id || "",
        timestamp: Date.now(),
        core_subject: Array.isArray(formData.core_subject)
          ? formData.core_subject.join(",")
          : formData.core_subject,
        e_material: formData.e_material,
        requested_points: want,
        remaining_points_after: null,
        status: "審核中",
        remarks: "",
        memo: "",
      };

      const resp = await axios.post(`${point}`, payload, { headers: { token } });
      if (resp.data.status === 200) {
        setSuccessMessage("申請成功！請等待管理員審核。");
        Swal.fire({
          icon: "success",
          title: "申請成功",
          text: "請等待管理員審核！",
        }).then(() => navigate("/member"));
      } else {
        Swal.fire({
          icon: "error",
          title: "提交失敗",
          text: resp.data.msg || "提交失敗，請稍後再試。",
        });
      }
    } catch (err) {
      console.error("提交失敗", err);
      Swal.fire({
        icon: "error",
        title: "伺服器錯誤",
        text: "提交失敗，請稍後再試。",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="container">
        <div className="banner mt100">
          <img src="/memberImg/548_photo_96289.png" alt="點數申請" />
        </div>

        <div className="m-auto">
          <p className="mt-3">
            Becker Taiwan 提供 CPA, CMA, EA 全系列中文課程於雲端，您可隨時線上觀看最新一期的中文課程影片！雲端化、隨選隨看的課程，能有效率地協助全球華人考生通過考試。
          </p>
          <h3 className="font-weight-bold mt-5 pb-3 ">
            <a
              href="https://a310.itraining.tw/"
              target="_blank"
              rel="noopener noreferrer"
            >
              「捷進 Becker 中文雲端教室」看課平台 (點此線上看課)
            </a>
          </h3>

          <h3 className="font-weight-bold mt-5 pb-3 ">影片上架時間點</h3>
          <p>每堂面授課皆有錄影，課後一週內上架至 Becker Taiwan 中文雲端教室，供補課或隨時隨地觀看。</p>
          <p>請留意各期課程適用的教材版本，例：CPA 第 53 期中文課程使用 2026 年考試的教材版本。</p>

          <h3 className="font-weight-bold mt-5 pb-3">影片開放時間</h3>
          <p>自影片上架日起，可觀看至該科目面授結束後半年（若該科目有新一期面授，則影片將提早被新課程取代）。</p>

          <h3 className="font-weight-bold mt-5 pb-3">點數看課機制</h3>
          <ul>
            <li><strong>為什麼需要申請點數？</strong> 部分學員選擇同時實體面授 / 雲端看課混成學習 (hybrid)，上課時數皆從個人總額度扣除。</li>
            <li><strong>線上看課扣點機制：</strong> 每堂課需使用 1 個點數觀看，點數一旦獲得，將直接從個人額度中扣除，無法取消或加回時數。</li>
            <li><strong>平台扣點與觀看方式：</strong> 扣點進入觀看後，可於「該堂課影片總時長 + 120 分鐘」內觀看，期間可暫停、倒帶、不重複扣點。</li>
          </ul>

          <h3 className="font-weight-bold mt-5 pb-3">所需點數額度</h3>
          <p>最新中文課程各科點數需求：</p>
          <ChineseCoursePointsTables coursePoints={coursePoints} />

          <h3 className="font-weight-bold mt-5 pb-3">版權與使用規範</h3>
          <p>Becker Taiwan 影片包含美國 Becker 原版教材，受美國著作權法保護。學員應遵守服務政策，嚴禁轉售、轉贈、轉讓及分享，違者將依法處理。</p>

          <h3 className="font-weight-bold mt-5 pb-3">特殊狀況提醒</h3>
          <p>請提早規畫申請准考證 / 預約考場，以確保應考座位和時間。若遇官方考試考場單位 (例：NASBA) 延遲回應，可聯絡 Becker Taiwan 團隊協助處理。</p>

          <h3 className="font-weight-bold mt-5 pb-3">平台疑難排解</h3>
          <p>若有任何問題，請聯絡 <a href="https://line.me/R/ti/p/%40vtz1908l" target="_blank" rel="noopener noreferrer">客服 LINE 帳號</a>。</p>
        </div>

        {/* ✅ 只顯示三個數字（加購明細仍保留 = 其餘不變動） */}
        <div className="mt-4 p-3 border rounded bg-light">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <span className="badge bg-secondary fs-6">總點數：{basePoints}</span>
            <span className="badge bg-dark fs-6">已申請使用點數：{usedPoints}</span>
            <span className="badge bg-success fs-5">剩餘可申請點數：{availablePoints}</span>
          </div>

          {/* 其餘不變動：加購點數/明細仍顯示（你若要也隱藏可再拿掉） */}
          <div className="mt-2 small text-muted">
            加購點數：{addonPoints}
          </div>
          {addonDetails.length > 0 && (
            <div className="mt-2 small text-muted">
              加購明細：
              {addonDetails.map(
                (a, i) =>
                  `#${a.add_id}（${a.memo}）+${a.points}點${
                    i < addonDetails.length - 1 ? "； " : ""
                  }`
              )}
            </div>
          )}
        </div>
      </div>

      {/* 申請紀錄 */}
      <div className="container mb100 mt100">
        <h5 className="mt-4">申請紀錄</h5>
        <table className="table table-striped table-bordered mt-3">
          <thead className="thead-dark">
            <tr>
              <th>申請時間</th>
              <th>科目</th>
              <th>申請點數</th>
              <th>狀態</th>
            </tr>
          </thead>
          <tbody>
            {myRequests.length > 0 ? (
              myRequests.map((r, i) => (
                <tr key={i}>
                  <td>
                    {r.timestamp && !isNaN(new Date(r.timestamp))
                      ? new Date(r.timestamp).toLocaleString()
                      : "—"}
                  </td>
                  <td>{r.core_subject}</td>
                  <td>{r.requested_points}</td>
                  <td>{r.status}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center">
                  尚無申請紀錄
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 申請表單 */}
      <div className="container mb100 mt100">
        <div className="beckertitlename mt100 mb-5">點數申請</div>
        <div className="card w-75 p-5 m-auto">
          <div className="card-body">
            {successMessage && (
              <div className="alert alert-success">{successMessage}</div>
            )}

            <form onSubmit={handleSubmit}>
              {/* CPA */}
              <div className="form-group mb-4">
                <label className="mb-2">學科（可多選）</label>
                <div className="checkbox-group">
                  {groups.CPA.map((c) => (
                    <label key={c.id} className="checkbox-inline me-3">
                      <input
                        type="checkbox"
                        value={c.course_name}
                        checked={formData.core_subjects.includes(c.course_name)}
                        onChange={handleCheckboxChange}
                      />{" "}
                      {c.course_name}
                    </label>
                  ))}
                </div>
              </div>

              {/* 專科 */}
              <div className="form-group mb-4">
                <label className="mb-2">專科（單選）</label>
                <div className="radio-group">
                  {specialtyOptions.map((sp) => (
                    <label key={sp} className="radio-inline me-3">
                      <input
                        type="radio"
                        name="specialty"
                        value={sp}
                        checked={formData.specialty === sp}
                        onChange={handleRadioChange}
                      />{" "}
                      {sp}
                    </label>
                  ))}
                </div>
              </div>

              {/* EA */}
              <div className="form-group mb-4">
                <label className="mb-2">EA</label>
                <div className="checkbox-group">
                  {groups.EA.map((c) => (
                    <label key={c.id} className="checkbox-inline me-3">
                      <input
                        type="checkbox"
                        value={c.course_name}
                        checked={formData.core_subjects.includes(c.course_name)}
                        onChange={handleCheckboxChange}
                      />{" "}
                      {c.course_name}
                    </label>
                  ))}
                </div>
              </div>

              {/* CMA */}
              <div className="form-group mb-4">
                <label className="mb-2">CMA</label>
                <div className="checkbox-group">
                  {groups.CMA.map((c) => (
                    <label key={c.id} className="checkbox-inline me-3">
                      <input
                        type="checkbox"
                        value={c.course_name}
                        checked={formData.core_subjects.includes(c.course_name)}
                        onChange={handleCheckboxChange}
                      />{" "}
                      {c.course_name}
                    </label>
                  ))}
                </div>
              </div>

              {/* ✅ 額外點數 UI 已移除（bonus 區塊整段拿掉） */}

              {/* 加購點數（保留 = 其餘不變動） */}
              <div className="form-group mb-4">
                <label className="mb-2">*是否使用加購點數?</label>
                <div className="radio-group">
                  <label className="radio-inline me-3">
                    <input
                      type="radio"
                      name="addp"
                      value="true"
                      checked={formData.addp === true}
                      onChange={handleRadioChange}
                    />{" "}
                    是
                  </label>
                  <label className="radio-inline">
                    <input
                      type="radio"
                      name="addp"
                      value="false"
                      checked={formData.addp === false}
                      onChange={handleRadioChange}
                    />{" "}
                    否
                  </label>
                </div>
              </div>

              {/* 電子講義（保持你原本寫法不動） */}
              <div className="form-group mb-4">
                <label className="mb-2">*是否領取電子講義?</label>
                <div className="radio-group">
                  <label className="radio-inline me-3">
                    <input
                      type="radio"
                      name="e_material"
                      value="false"
                      checked={formData.e_material === false}
                      onChange={handleRadioChange}
                    />{" "}
                    是
                  </label>
                  <label className="radio-inline">
                    <input
                      type="radio"
                      name="e_material"
                      value="true"
                      checked={formData.e_material === true}
                      onChange={handleRadioChange}
                    />{" "}
                    否
                  </label>
                </div>
              </div>

              {/* 申請點數 */}
              <div className="form-group mb-4">
                <label className="mb-2">*申請點數</label>
                <input
                  type="number"
                  className="form-control"
                  name="requested_points"
                  value={formData.requested_points}
                  min="0"
                  step="1"
                  onChange={handleRequestedPointsChange}
                />
                <small className="text-muted">
                  預設會依「學科＋專科」自動計算，您也可自行調整為更少或 0 點（例如只申請講義）。
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={isSubmitting}
              >
                {isSubmitting ? "提交中..." : "提交申請"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
